/**
 * VelocityGauge — P11 Velocity Score (0-100) circular gauge with component breakdown.
 */
import { Trophy } from "lucide-react";

const componentMeta = {
  yield: { label: "Yield", icon: "📈", desc: "Rental income relative to property value" },
  lag: { label: "Speed", icon: "⚡", desc: "How fast you hit cashflow-positive" },
  ltv: { label: "Leverage", icon: "🏦", desc: "Lower LTV across portfolio = higher score" },
  surplus: { label: "Buffer", icon: "💰", desc: "Months portfolio CF stays positive" },
  mix: { label: "Diversification", icon: "🏘", desc: "Number of properties purchased" },
};

export default function VelocityGauge({ velocity }) {
  if (!velocity) return null;
  const { score = 0, color = "amber", label, components = {}, max = {} } = velocity;
  const colorMap = {
    green: { ring: "#0B7B6E", bg: "rgba(11, 123, 110, 0.1)", text: "#0B7B6E" },
    amber: { ring: "#C85A32", bg: "rgba(200, 90, 50, 0.1)", text: "#C85A32" },
    red: { ring: "#e11d48", bg: "rgba(225, 29, 72, 0.1)", text: "#e11d48" },
  }[color];

  const circumference = 2 * Math.PI * 70;
  const dash = (score / 100) * circumference;

  return (
    <div className="card-flat p-5" data-testid="velocity-gauge">
      <div className="flex items-center gap-2 mb-3">
        <Trophy size={18} style={{ color: colorMap.text }} />
        <h4 className="font-serif text-lg">Velocity Score</h4>
      </div>
      <div className="flex flex-col md:flex-row items-center gap-6">
        {/* SVG gauge */}
        <div className="relative flex-shrink-0">
          <svg width="180" height="180" className="-rotate-90">
            <circle cx="90" cy="90" r="70" stroke="#e5e1d6" strokeWidth="14" fill="none" />
            <circle
              cx="90"
              cy="90"
              r="70"
              stroke={colorMap.ring}
              strokeWidth="14"
              fill="none"
              strokeDasharray={`${dash} ${circumference}`}
              strokeLinecap="round"
              style={{ transition: "stroke-dasharray 600ms ease-out" }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-4xl font-serif font-bold" style={{ color: colorMap.text }} data-testid="velocity-score-value">
              {score}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">/ 100</div>
          </div>
        </div>

        {/* Breakdown */}
        <div className="flex-1 w-full space-y-2">
          <div
            className="rounded-md px-3 py-1.5 inline-block text-xs font-medium"
            style={{ background: colorMap.bg, color: colorMap.text }}
          >
            {label}
          </div>
          <div className="space-y-1.5 mt-2">
            {Object.entries(components).map(([k, v]) => {
              const meta = componentMeta[k] || { label: k, icon: "•", desc: "" };
              const maxV = max[k] || 25;
              const pct = (v / maxV) * 100;
              return (
                <div key={k} data-testid={`velocity-component-${k}`}>
                  <div className="flex items-center justify-between text-xs">
                    <span title={meta.desc}>
                      {meta.icon} {meta.label}
                    </span>
                    <span className="font-mono text-muted-foreground">
                      {v.toFixed(1)} <span className="opacity-60">/ {maxV}</span>
                    </span>
                  </div>
                  <div className="h-1.5 bg-[#e5e1d6] rounded-full overflow-hidden mt-0.5">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${pct}%`, background: colorMap.ring }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
